import logger from '../utils/logger';
import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { GlobalStateContext } from '../utils/GlobalStateContext';
import { getUserId } from '../utils/utils';
import { ShieldCheck, ShieldOff, Shield } from 'lucide-react';
import AdminSecurityTab from './AdminSecurityTab';
import '../style/AdminDashboard.css';

const ROLE_LABELS = {
    clinician: 'Clinician',
    lab_tech: 'Lab Tech',
    rad_tech: 'Rad Tech',
    biller: 'Biller',
    medical_assistant: 'Medical Assistant',
    office_assistant: 'Office Assistant',
};

export default function AdminDashboard() {
    const BASE_URL = process.env.REACT_APP_API_URL;
    const { currentUser } = useContext(GlobalStateContext);
    const user = getUserId(currentUser);
    const navigate = useNavigate();

    const [activeTab, setActiveTab] = useState('patients');

    // --- Lab Orders state ---
    const today = new Date().toISOString().slice(0, 10);
    const firstOfMonth = today.slice(0, 8) + '01';
    const [labStartDate, setLabStartDate] = useState(firstOfMonth);
    const [labEndDate, setLabEndDate] = useState(today);
    const [labOrders, setLabOrders] = useState([]);
    const [labLoading, setLabLoading] = useState(false);
    const [labError, setLabError] = useState('');
    const [labExpandedOrder, setLabExpandedOrder] = useState(null);

    // --- Patients state ---
    const [patients, setPatients] = useState([]);
    const [patientSearch, setPatientSearch] = useState('');
    const [keepPatient, setKeepPatient] = useState(null);   // green
    const [deletePatient, setDeletePatient] = useState(null); // red
    const [mergeLoading, setMergeLoading] = useState(false);
    const [mergeError, setMergeError] = useState('');
    const [mergeSuccess, setMergeSuccess] = useState('');

    // --- Staff state ---
    const [staff, setStaff] = useState([]);
    const [staffFilter, setStaffFilter] = useState('all');
    const [showInactive, setShowInactive] = useState(false);
    const [deactivateError, setDeactivateError] = useState('');
    const [deactivateSuccess, setDeactivateSuccess] = useState('');
    const [mfaResetError, setMfaResetError] = useState('');
    const [mfaResetSuccess, setMfaResetSuccess] = useState('');

    useEffect(() => {
        if (activeTab === 'patients') fetchPatients();
        if (activeTab === 'staff') fetchStaff();
    }, [activeTab]);

    const fetchPatients = async () => {
        try {
            const res = await fetch(`${BASE_URL}/admin/patients?user=${user}&type=${currentUser?.type}`);
            const data = await res.json();
            setPatients(Array.isArray(data) ? data : []);
        } catch (err) {
            logger.error('Error fetching patients:', err);
        }
    };

    const fetchStaff = async () => {
        try {
            const res = await fetch(`${BASE_URL}/admin/staff?user=${user}&type=${currentUser?.type}`);
            const data = await res.json();
            setStaff(Array.isArray(data) ? data : []);
        } catch (err) {
            logger.error('Error fetching staff:', err);
        }
    };

    const handlePatientClick = (patient) => {
        // Reset merge messages
        setMergeError('');
        setMergeSuccess('');

        if (!keepPatient) {
            setKeepPatient(patient);
            return;
        }
        if (keepPatient.id === patient.id) {
            // Clicking the green patient again deselects it
            setKeepPatient(null);
            setDeletePatient(null);
            return;
        }
        setDeletePatient(patient);
    };

    const resetMergeSelection = () => {
        setKeepPatient(null);
        setDeletePatient(null);
        setMergeError('');
        setMergeSuccess('');
    };

    const handleMerge = async () => {
        if (!keepPatient || !deletePatient) return;

        const confirmed = window.confirm(
            `Merge patients?\n\nKEEP: ${keepPatient.first_name} ${keepPatient.last_name} (ID: ${keepPatient.id})\nDELETE: ${deletePatient.first_name} ${deletePatient.last_name} (ID: ${deletePatient.id})\n\nAll records (orders, labs, billing, payments) will be reassigned to the kept patient.\n\nThis cannot be undone.`
        );
        if (!confirmed) return;

        setMergeLoading(true);
        setMergeError('');
        setMergeSuccess('');

        try {
            const res = await fetch(`${BASE_URL}/admin/mergePatients`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    keepId: keepPatient.id,
                    deleteId: deletePatient.id,
                    user,
                    type: currentUser?.type,
                }),
            });

            const data = await res.json();

            if (res.ok) {
                setMergeSuccess(`Successfully merged: ${deletePatient.first_name} ${deletePatient.last_name} → ${keepPatient.first_name} ${keepPatient.last_name}`);
                resetMergeSelection();
                fetchPatients();
            } else {
                setMergeError(data.error || 'Merge failed');
            }
        } catch (err) {
            setMergeError('Network error during merge');
        } finally {
            setMergeLoading(false);
        }
    };

    const getBorderStyle = (patient) => {
        if (keepPatient?.id === patient.id) return 'admin-patient-card keep';
        if (deletePatient?.id === patient.id) return 'admin-patient-card delete';
        return 'admin-patient-card';
    };

    const filteredPatients = patients.filter((p) => {
        const q = patientSearch.toLowerCase();
        return (
            p.first_name?.toLowerCase().includes(q) ||
            p.last_name?.toLowerCase().includes(q) ||
            String(p.id).includes(q)
        );
    });

    const handleDeactivateStaff = async (staffMember) => {
        const confirmed = window.confirm(
            `Deactivate ${staffMember.first_name} ${staffMember.last_name} (${ROLE_LABELS[staffMember.role] || staffMember.role}, ID: ${staffMember.id})?\n\nThis will mark them as inactive. They will remain in the system for audit purposes.`
        );
        if (!confirmed) return;

        setDeactivateError('');
        setDeactivateSuccess('');

        try {
            const res = await fetch(`${BASE_URL}/admin/deactivateStaff`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ role: staffMember.role, id: staffMember.id, user, type: currentUser?.type }),
            });
            const data = await res.json();
            if (res.ok) {
                setDeactivateSuccess(`${staffMember.first_name} ${staffMember.last_name} has been deactivated.`);
                fetchStaff();
            } else {
                setDeactivateError(data.error || 'Deactivation failed');
            }
        } catch {
            setDeactivateError('Network error during deactivation');
        }
    };

    const handleResetMfa = async (staffMember) => {
        const confirmed = window.confirm(
            `Reset MFA for ${staffMember.first_name} ${staffMember.last_name} (${ROLE_LABELS[staffMember.role] || staffMember.role})?\n\nThis will disable two-factor authentication for their account. They will need to re-enroll the next time they want MFA enabled.\n\nContinue?`
        );
        if (!confirmed) return;

        setMfaResetError('');
        setMfaResetSuccess('');

        try {
            const res = await fetch(`${BASE_URL}/admin/resetMfa`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ role: staffMember.role, id: staffMember.id, user, type: currentUser?.type }),
            });
            const data = await res.json();
            if (res.ok) {
                setMfaResetSuccess(`MFA has been reset for ${staffMember.first_name} ${staffMember.last_name}.`);
                fetchStaff();
            } else {
                setMfaResetError(data.error || 'MFA reset failed');
            }
        } catch {
            setMfaResetError('Network error during MFA reset');
        }
    };

    const handleToggleEnforcement = async (staffMember) => {
        const enabling = !staffMember.mfa_enforced;
        const confirmed = window.confirm(
            `${enabling ? 'Enforce' : 'Remove enforcement of'} MFA for ${staffMember.first_name} ${staffMember.last_name}?\n\n${enabling
                ? 'They will be required to set up or use MFA at their next login.'
                : 'They will no longer be required to use MFA.'}`
        );
        if (!confirmed) return;

        setMfaResetError('');
        setMfaResetSuccess('');

        try {
            const res = await fetch(`${BASE_URL}/admin/toggleMfaEnforcement`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ role: staffMember.role, id: staffMember.id, enforce: enabling, user, type: currentUser?.type }),
            });
            const data = await res.json();
            if (res.ok) {
                setMfaResetSuccess(`MFA enforcement ${enabling ? 'enabled' : 'disabled'} for ${staffMember.first_name} ${staffMember.last_name}.`);
                setStaff(prev => prev.map(s =>
                    s.role === staffMember.role && s.id === staffMember.id
                        ? { ...s, mfa_enforced: data.mfa_enforced }
                        : s
                ));
            } else {
                setMfaResetError(data.error || 'Toggle failed');
            }
        } catch {
            setMfaResetError('Network error during enforcement toggle');
        }
    };

    const fetchLabOrders = async () => {
        if (!labStartDate || !labEndDate) return;
        setLabLoading(true);
        setLabError('');
        try {
            const res = await fetch(
                `${BASE_URL}/admin/labOrders?startDate=${labStartDate}&endDate=${labEndDate}&user=${user}&type=${currentUser?.type}`
            );
            const data = await res.json();
            if (res.ok) {
                setLabOrders(Array.isArray(data) ? data : []);
            } else {
                setLabError(data.error || 'Failed to load lab orders');
            }
        } catch {
            setLabError('Network error');
        } finally {
            setLabLoading(false);
        }
    };

    // Group flat rows (one per item) into orders with nested items array
    const groupedLabOrders = labOrders.reduce((acc, row) => {
        const existing = acc.find(o => o.order_id === row.order_id);
        const item = row.item_id
            ? { id: row.item_id, item_type: row.item_type, item_name: row.item_name, cpt_code: row.cpt_code }
            : null;
        if (existing) {
            if (item) existing.items.push(item);
        } else {
            acc.push({
                order_id: row.order_id,
                created_at: row.created_at,
                patient_name: row.patient_name,
                patient_id: row.patient_id,
                ordering_clinician: row.ordering_clinician,
                diagnosis: row.diagnosis,
                billing_type: row.billing_type,
                priority: row.priority,
                status: row.status,
                specimen_date: row.specimen_date,
                fasting: row.fasting,
                referred_to: row.referred_to,
                sent_at: row.sent_at,
                items: item ? [item] : [],
            });
        }
        return acc;
    }, []);

    const formatDate = (raw) => {
        if (!raw) return '—';
        const d = new Date(raw);
        return d.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    };

    const filteredStaff = staff
        .filter((s) => staffFilter === 'all' || s.role === staffFilter)
        .filter((s) => showInactive || s.is_active !== 0);

    return (
        <div className="admin-dashboard">
            <ToastContainer position="top-right" autoClose={3000} />
            <div className="admin-header">
                <button className="admin-back-btn" onClick={() => navigate('/home')}>← Back</button>
                <h1>Admin Dashboard</h1>
            </div>

            <div className="admin-tabs">
                <button
                    className={`admin-tab ${activeTab === 'patients' ? 'active' : ''}`}
                    onClick={() => setActiveTab('patients')}
                >
                    Patients
                </button>
                <button
                    className={`admin-tab ${activeTab === 'staff' ? 'active' : ''}`}
                    onClick={() => setActiveTab('staff')}
                >
                    Staff
                </button>
                <button
                    className={`admin-tab ${activeTab === 'security' ? 'active' : ''}`}
                    onClick={() => setActiveTab('security')}
                >
                    Security
                </button>
                <button
                    className={`admin-tab ${activeTab === 'labOrders' ? 'active' : ''}`}
                    onClick={() => setActiveTab('labOrders')}
                >
                    Lab Orders
                </button>
            </div>

            {/* ── PATIENTS TAB ── */}
            {activeTab === 'patients' && (
                <div className="admin-section">
                    <div className="admin-patients-toolbar">
                        <input
                            type="text"
                            placeholder="Search by name or ID..."
                            value={patientSearch}
                            onChange={(e) => setPatientSearch(e.target.value)}
                            className="admin-search-input"
                        />
                        <span className="admin-count">{filteredPatients.length} patients</span>
                    </div>

                    {/* Merge controls */}
                    <div className="admin-merge-panel">
                        <div className="admin-merge-selections">
                            <div className="admin-merge-slot keep-slot">
                                <span className="merge-slot-label">Keep (green)</span>
                                <span className="merge-slot-value">
                                    {keepPatient
                                        ? `${keepPatient.first_name} ${keepPatient.last_name} — ID ${keepPatient.id}`
                                        : 'Click a patient card to select'}
                                </span>
                            </div>
                            <div className="admin-merge-slot delete-slot">
                                <span className="merge-slot-label">Delete (red)</span>
                                <span className="merge-slot-value">
                                    {deletePatient
                                        ? `${deletePatient.first_name} ${deletePatient.last_name} — ID ${deletePatient.id}`
                                        : 'Click a second patient card to select'}
                                </span>
                            </div>
                        </div>
                        <div className="admin-merge-actions">
                            <button
                                className="admin-merge-btn"
                                disabled={!keepPatient || !deletePatient || mergeLoading}
                                onClick={handleMerge}
                            >
                                {mergeLoading ? 'Merging...' : 'Merge Patients'}
                            </button>
                            <button className="admin-reset-btn" onClick={resetMergeSelection}>
                                Reset Selection
                            </button>
                        </div>
                        {mergeError && <p className="admin-merge-error">{mergeError}</p>}
                        {mergeSuccess && <p className="admin-merge-success">{mergeSuccess}</p>}
                    </div>

                    <div className="admin-patient-grid">
                        {filteredPatients.map((patient) => (
                            <div
                                key={patient.id}
                                className={getBorderStyle(patient)}
                                onClick={() => handlePatientClick(patient)}
                            >
                                <div className="patient-card-name">
                                    {patient.first_name} {patient.last_name}
                                </div>
                                <div className="patient-card-detail">ID: {patient.id}</div>
                                <div className="patient-card-detail">DOB: {patient.dob}</div>
                                <div className="patient-card-detail">
                                    {patient.street_number} {patient.street_name}, {patient.city}, {patient.state} {patient.zip}
                                </div>
                                <div className="patient-card-detail">{patient.primary_phone || patient.cell_phone}</div>
                                <div className="patient-card-detail">{patient.email}</div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* ── STAFF TAB ── */}
            {activeTab === 'staff' && (
                <div className="admin-section">
                    <div className="admin-staff-filter">
                        <label>Filter by role:</label>
                        <select
                            value={staffFilter}
                            onChange={(e) => setStaffFilter(e.target.value)}
                            className="admin-role-select"
                        >
                            <option value="all">All Roles</option>
                            {Object.entries(ROLE_LABELS).map(([value, label]) => (
                                <option key={value} value={value}>{label}</option>
                            ))}
                        </select>
                        <label className="admin-inactive-toggle">
                            <input
                                type="checkbox"
                                checked={showInactive}
                                onChange={(e) => setShowInactive(e.target.checked)}
                            />
                            Show inactive
                        </label>
                        <span className="admin-count">{filteredStaff.length} users</span>
                    </div>

                    {deactivateError && <p className="admin-merge-error">{deactivateError}</p>}
                    {deactivateSuccess && <p className="admin-merge-success">{deactivateSuccess}</p>}
                    {mfaResetError && <p className="admin-merge-error">{mfaResetError}</p>}
                    {mfaResetSuccess && <p className="admin-merge-success">{mfaResetSuccess}</p>}

                    <table className="admin-staff-table">
                        <thead>
                            <tr>
                                <th>Role</th>
                                <th>ID</th>
                                <th>First Name</th>
                                <th>Last Name</th>
                                <th>Title</th>
                                <th>Email</th>
                                <th>MFA</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredStaff.map((s, i) => (
                                <tr key={`${s.role}-${s.id}-${i}`} className={s.is_active === 0 ? 'staff-row-inactive' : ''}>
                                    <td>
                                        <span className={`role-badge role-${s.role}`}>{ROLE_LABELS[s.role] || s.role}</span>
                                        {s.is_active === 0 && <span className="staff-inactive-badge">Inactive</span>}
                                    </td>
                                    <td>{s.id}</td>
                                    <td>{s.first_name}</td>
                                    <td>{s.last_name}</td>
                                    <td>{s.title || '—'}</td>
                                    <td>{s.email}</td>
                                    <td className="staff-mfa-cell">
                                        {s.mfa_enabled
                                            ? <ShieldCheck size={16} className="staff-mfa-on"       title="MFA enabled" />
                                            : s.mfa_enforced
                                                ? <Shield  size={16} className="staff-mfa-enforced" title="MFA required — not yet set up" />
                                                : <ShieldOff size={16} className="staff-mfa-off"    title="MFA not enabled" />}
                                    </td>
                                    <td className="staff-action-cell">
                                        <button
                                            className={`staff-enforce-btn ${s.mfa_enforced ? 'staff-enforce-btn-on' : ''}`}
                                            title={s.mfa_enforced ? 'Remove MFA requirement' : 'Require MFA for this user'}
                                            onClick={() => handleToggleEnforcement(s)}
                                        >
                                            <Shield size={14} />
                                        </button>
                                        {s.mfa_enabled === 1 && (
                                            <button
                                                className="staff-mfa-reset-btn"
                                                title={`Reset MFA for ${s.first_name} ${s.last_name}`}
                                                onClick={() => handleResetMfa(s)}
                                            >
                                                <ShieldOff size={14} />
                                            </button>
                                        )}
                                        {s.is_active !== 0 && (
                                            <button
                                                className="staff-deactivate-btn"
                                                title={`Deactivate ${s.first_name} ${s.last_name}`}
                                                onClick={() => handleDeactivateStaff(s)}
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                    <polyline points="3 6 5 6 21 6"/>
                                                    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
                                                    <path d="M10 11v6"/>
                                                    <path d="M14 11v6"/>
                                                    <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
                                                </svg>
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
            {/* ── SECURITY TAB ── */}
            {activeTab === 'security' && (
                <AdminSecurityTab
                    BASE_URL={BASE_URL}
                    currentUser={currentUser}
                    user={user}
                />
            )}

            {/* Rad Tech / Lab Machine Availability moved to /availability
                (components/StaffAvailability.js) so techs can manage their own
                calendars without being granted the admin role. */}

            {/* ── LAB ORDERS TAB ── */}
            {activeTab === 'labOrders' && (
                <div className="admin-section">
                    <div className="lab-orders-toolbar">
                        <label className="lab-orders-label">From</label>
                        <input
                            type="date"
                            value={labStartDate}
                            onChange={(e) => setLabStartDate(e.target.value)}
                            className="admin-search-input"
                        />
                        <label className="lab-orders-label">To</label>
                        <input
                            type="date"
                            value={labEndDate}
                            onChange={(e) => setLabEndDate(e.target.value)}
                            className="admin-search-input"
                        />
                        <button
                            className="lab-orders-fetch-btn"
                            onClick={fetchLabOrders}
                            disabled={labLoading}
                        >
                            {labLoading ? 'Loading…' : 'Run Query'}
                        </button>
                        {groupedLabOrders.length > 0 && (
                            <span className="admin-count">{groupedLabOrders.length} orders</span>
                        )}
                    </div>

                    {labError && <p className="admin-merge-error">{labError}</p>}

                    {groupedLabOrders.length > 0 && (
                        <table className="admin-staff-table lab-orders-table">
                            <thead>
                                <tr>
                                    <th>Order ID</th>
                                    <th>Created</th>
                                    <th>Patient</th>
                                    <th>Ordering Clinician</th>
                                    <th>Diagnosis</th>
                                    <th>Priority</th>
                                    <th>Status</th>
                                    <th>Billing</th>
                                    <th>Fasting</th>
                                    <th>Sent At</th>
                                    <th>Items</th>
                                </tr>
                            </thead>
                            <tbody>
                                {groupedLabOrders.map((order) => (
                                    <React.Fragment key={order.order_id}>
                                        <tr
                                            className={`lab-order-row ${labExpandedOrder === order.order_id ? 'lab-order-row-expanded' : ''}`}
                                            onClick={() => setLabExpandedOrder(
                                                labExpandedOrder === order.order_id ? null : order.order_id
                                            )}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            <td>#{order.order_id}</td>
                                            <td style={{ whiteSpace: 'nowrap', fontSize: '0.82rem', color: '#6c757d' }}>
                                                {formatDate(order.created_at)}
                                            </td>
                                            <td>
                                                <span style={{ fontWeight: 600 }}>{order.patient_name}</span>
                                                <span style={{ fontSize: '0.78rem', color: '#adb5bd', marginLeft: 6 }}>
                                                    #{order.patient_id}
                                                </span>
                                            </td>
                                            <td>{order.ordering_clinician}</td>
                                            <td style={{ maxWidth: 200, wordBreak: 'break-word' }}>{order.diagnosis}</td>
                                            <td>
                                                <span className={`role-badge ${order.priority === 'STAT' ? 'lab-priority-stat' : 'lab-priority-routine'}`}>
                                                    {order.priority}
                                                </span>
                                            </td>
                                            <td>
                                                <span className={`role-badge lab-status-${order.status}`}>
                                                    {order.status}
                                                </span>
                                            </td>
                                            <td style={{ fontSize: '0.85rem' }}>{order.billing_type === 'client_bill' ? 'Client Bill' : 'Insurance'}</td>
                                            <td style={{ fontSize: '0.85rem' }}>{order.fasting || '—'}</td>
                                            <td style={{ fontSize: '0.82rem', color: '#6c757d', whiteSpace: 'nowrap' }}>
                                                {order.sent_at ? formatDate(order.sent_at) : '—'}
                                            </td>
                                            <td style={{ fontSize: '0.82rem', color: '#6c757d' }}>
                                                {order.items.length} item{order.items.length !== 1 ? 's' : ''} ▾
                                            </td>
                                        </tr>
                                        {labExpandedOrder === order.order_id && (
                                            <tr className="lab-order-items-row">
                                                <td colSpan={11}>
                                                    <div className="lab-order-items-panel">
                                                        {order.items.length === 0 ? (
                                                            <span style={{ color: '#adb5bd', fontSize: '0.85rem' }}>No items recorded.</span>
                                                        ) : (
                                                            <table className="lab-items-table">
                                                                <thead>
                                                                    <tr>
                                                                        <th>Type</th>
                                                                        <th>Name</th>
                                                                        <th>CPT Code</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {order.items.map((item) => (
                                                                        <tr key={item.id}>
                                                                            <td>
                                                                                <span className={`role-badge lab-item-${item.item_type}`}>
                                                                                    {item.item_type}
                                                                                </span>
                                                                            </td>
                                                                            <td>{item.item_name}</td>
                                                                            <td style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                                                                                {item.cpt_code || '—'}
                                                                            </td>
                                                                        </tr>
                                                                    ))}
                                                                </tbody>
                                                            </table>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                ))}
                            </tbody>
                        </table>
                    )}

                    {!labLoading && labOrders.length === 0 && !labError && (
                        <p style={{ color: '#adb5bd', marginTop: 24 }}>Select a date range and click Run Query.</p>
                    )}
                </div>
            )}
        </div>
    );
}
